import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { CursoComplementar } from '../../domain';
import { HookProps, HookResult } from './curso-complementar.types';

// meia hora em milissegundos
const TEMPO_DEDUPING = 1800000;

export const useCursoComplementar = <
  T extends CursoComplementar = CursoComplementar,
>({
  precondicao,
  fonteDados,
  codigoCurso,
}: HookProps<T>): HookResult<T> => {
  const { data, isValidating, error, mutate } = useSWR<T>(
    precondicao() ? ['consultar-curso-complementar', codigoCurso] : null,
    () => fonteDados(codigoCurso),
    {
      revalidateOnReconnect: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      dedupingInterval: TEMPO_DEDUPING,
    },
  );

  const erro = !!error;

  const pendente = useMemo(
    () => (!data && !erro) || (erro && isValidating),
    [data, erro, isValidating],
  );

  const retentar = useCallback(() => {
    mutate();
  }, [mutate]);

  const modificar = useCallback(
    (
      modificarCursoComplementar: (cursoComplementarAtual: T | undefined) => T,
    ) => {
      mutate(modificarCursoComplementar, {
        revalidate: false,
      });
    },
    [mutate],
  );

  const resetar = useCallback(() => {
    mutate(undefined);
  }, [mutate]);

  const asyncCursoComplementar = useMemo(
    () => ({
      value: data ?? null,
      pendente,
      erro: error,
    }),
    [data, error, pendente],
  );

  return {
    asyncCursoComplementar,
    retentar,
    modificar,
    resetar,
  };
};
